import { Box, Grid, InputAdornment } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import 'moment/locale/th';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { routeName } from 'routes/routes-name';

/** TRANSLATION*/
import { useTranslation } from 'react-i18next';

/**API */
import ClinicApi from 'api/master/clinic.api';
import ReportApi, { FindAllReportInterface } from 'api/report.api';
import { getBranch, toBuddhistYear } from 'utils/app.utils';

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard';
import { useDownloadExcel } from 'component/Excel/hooks/useExcel';

/**STYLE */
/** CONSTANTS */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker';
import DropdownButton from 'new-components/buttons/dropdown-button';
import MainButton from 'new-components/buttons/main-button';
import SelectInput from 'new-components/inputs/select-input';
import TextInput from 'new-components/inputs/text-input';
import PatientDateOfBirthList from './AccessChannelList';
import ExportToExcel from './ExportToExcel';

type Doctors = {
    userId: number
    firstname: string
    lastname: string
    fullname: string
}

export interface DentistInterface {
    dentistId: number
    dentistFullname: string
}

export default function AccessChanel() {
    const { t } = useTranslation();

    const history = useHistory();
    const componentRef = useRef<HTMLDivElement>(null);

    const momentNowRange = [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
    const momentDate = () => momentNowRange

    const [branchId, setBranchId] = useState<any>(getBranch());
    const [branches, setBranches] = useState<any[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const [sortBy, setSortBy] = useState<string>('createAt');
    const [sortType, setSortType] = useState<string>('ASC');

    const [doctors, setDoctors] = useState<Doctors[]>([]);
    const [doctorId, setDoctorId] = useState<number>(0);

    const [search, setSearch] = useState<string>('');
    const [patientAccessChannel, setPatientAccessChannel] = useState<any[]>([]);
    const [patientAccessChannelExport, setPatientAccessChannelExport] = useState<any[]>([]);
    const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
    const [accessChannelPage, setAccessChannelPage] = useState<number>(1);
    const [accessChannelPageLimit, setAccessChannelPageLimit] = useState<number>(10);
    const [labSecondPage, setLabSecondPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [fetch, setFetch] = useState<boolean>(false);

    const verifyPermission = async () => {
        const condition: FindAllReportInterface = {
            page: 1, pageLimit: 200
        }
        ReportApi.reportList(condition)
            .then(({ data }) => {
                const check = _.find(data, (val: any) => String(val.reportKey) === 'ACCESS_CHANNEL') ? true : false
                if (!check) {
                    history.push(routeName.report)
                    return false
                }
            })

        ClinicApi.findAllBranches()
            .then(({ data }) => setBranches(data))
            .catch((e) => { return })

        ClinicApi.findAllDoctors('', '', rangeDate[0], rangeDate[1])
            .then((resp) => setDoctors(resp.data))
            .catch((e) => { return })

        return true
    }

    useEffect(() => {
        verifyPermission()
    }, [])

    const fetchAccessChannelLogic = async (setDataFunction: any, exportCondition?: number) => {
        await verifyPermission();
        let condition: FindAllReportInterface = {
            page: accessChannelPage,
            pageLimit: accessChannelPageLimit,
            search: search,
            dateStart: rangeDate[0],
            dateEnd: rangeDate[1]
        };

        if (branchId) condition = { ...condition, branchId: branchId }
        if (exportCondition) {
            condition = { ...condition, export: exportCondition };
        }
        if (sortBy) condition = { ...condition, sortBy: sortBy };
        if (sortType) condition = { ...condition, sortType: sortType };

        return ReportApi.reportAccessChannel(condition)
            .then(({ headers, data }) => {
                setDataFunction(data);
                setRowCount(headers['x-total']);
            });
    };

    const fetchAccessChannelExport = useCallback(async () => {
        try {
            await fetchAccessChannelLogic(setPatientAccessChannelExport, 1);
        } catch (e) {
            setLoading(false);
        } finally {
            setLoading(false);
            setFetch(false);
        }
    }, [fetch]);

    const fetchAccessChannel = useCallback(async () => {
        try {
            await fetchAccessChannelLogic(setPatientAccessChannel);
        } catch (e) {
            setLoading(false);
        } finally {
            setLoading(false);
            setFetch(false);
        }
    }, [accessChannelPage, labSecondPage, accessChannelPageLimit, fetch]);

    const { onDownload } = useDownloadExcel({
        tableExportRef: componentRef.current,
        filename: `${t(`REPORT.REPORT_NAME.ACCESS_CHANNEL`)}`,
        sheetName: `${t(`REPORT.REPORT_NAME.ACCESS_CHANNEL`)}`
    })

    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const handleChangeRowsPerPage = (limit: number) => setAccessChannelPageLimit(limit)
    const handleChangePage = (val: number) => setAccessChannelPage(val)

    useEffect(() => {
        fetchAccessChannel()
    }, [fetchAccessChannel])

    const exportExcel = async () => {
        setLoading(true);
        try {
            await ExportToExcel({
                data: patientAccessChannel,
                filter: [
                    t(`REPORT.REPORT_NAME.ACCESS_CHANNEL`),
                    branchId === 0 ? t('REPORT.MONTH.ALL') : `${t('REPORT.FILTER.BRANCH')}: ${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}`,
                    rangeDate[0] ? toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY') : '-',
                    rangeDate[1] ? toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY') : '-',
                ],
                t: t,
            });
        } catch (error) {
            console.error('Error exporting to Excel:', error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Box>
            <HeaderCard text={t(`REPORT.REPORT_NAME.ACCESS_CHANNEL`)} />
            <Box padding="16px">
                <Grid container spacing="16px">
                    <Grid item xs={12} sm={6} md={3}>
                        <TextInput
                            label={t('INPUT.SEARCH_PATIENT')}
                            value={search}
                            fullWidth
                            onChange={(event) => setSearch(event.target.value)}
                            inputProps={{
                                value: search,
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SelectInput
                            onChange={(e) => {
                                setBranchId(e.target.value)
                                setAccessChannelPage(1)
                            }}
                            options={_.map(branches, (item) => ({ value: item.branchId, name: item.branchName }))}
                            value={branchId}
                            defaultValue={`${_.get(_.find(branches, { branchId: branchId }), 'branchName' || 'branchNameEn')} `}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <InputSecondNewRangePicker
                            inputHeight={40}
                            value={rangeDate || emptyRangePicker}
                            onchange={(val: any) => {
                                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                            }}
                            label={''}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <MainButton
                                title={t('INPUT.SEARCH')}
                                onClick={() => {
                                    setFetch(true)
                                    setAccessChannelPage(1)
                                    setLoading(true)
                                }}
                                type={'primary'}
                                variant={'contained'}
                                style={{ width: '120px' }}
                            />
                            <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                                if (e === 'Excel') {
                                    exportExcel();
                                } else if (e === 'PDF') {
                                    const style = document.createElement("style");
                                    style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
                                    document.head.appendChild(style);
                                    window.print();
                                    document.head.removeChild(style);

                                }
                            }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                        </Box>
                    </Grid>
                </Grid>
                <Box marginTop="16px">
                    <PatientDateOfBirthList
                        page={accessChannelPage}
                        pageLimit={accessChannelPageLimit}
                        rowCount={rowCount}
                        sortBy={sortBy}
                        sortType={sortType}
                        data={patientAccessChannel}
                        dataExport={patientAccessChannelExport}
                        onRequestSort={onRequestSort}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        handleChangePage={handleChangePage}
                        componentRef={componentRef}
                        filter={[`${t(`REPORT.REPORT_NAME.ACCESS_CHANNEL`)}`, branchId === 0 ? `${t('REPORT.MONTH.ALL')}` : `${t('REPORT.FILTER.BRANCH')}: ${_.get(_.find(branches, { branchId: branchId }), 'branchName' || 'branchNameEn')} `, rangeDate[0] ? toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY') : '-', rangeDate[1] ? toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY') : '-']}
                    />
                </Box>
            </Box>
        </Box>
    )
}